import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { from, Observable, switchMap } from 'rxjs';
import { MenuItem } from './models/menu-item';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryParamsKeys } from './enums';
import { AnimationOptions } from 'ngx-lottie';
import { Certificate } from './models/certificate';
import { LingRegExps } from './data/ling-reg-exps';
import { SecurityContext } from '@angular/core';

export function handlePluralTranslation(
  amount: number | undefined = 0,
  key: string
): string {
  return Number(amount ?? 0) === 1 ? key : key + 'Plural';
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function compareObjects<T extends object>(
  obj1: T,
  obj2: T,
  keys?: (keyof T)[]
): boolean {
  let equal = false;
  for (const key of keys ?? (Object.keys(obj1) as (keyof T)[])) {
    if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
      equal = obj1[key] === obj2[key];
    }
    if (!equal) {
      return equal;
    }
  }
  return equal;
}

export function mbToBytes(amount: number): number {
  return Math.floor(amount * 1024 * 1024);
}

export function bytesToMb(amount: number): number {
  return +(amount / 1024 / 1024).toFixed(2);
}

export function isElementVisible(element: HTMLElement, topOffset = 0): boolean {
  if (!element) {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return (
    (rect.top >= topOffset &&
      rect.top <= document.documentElement.clientHeight / 2 + topOffset) ||
    (rect.bottom >=
      document.documentElement.clientHeight / 2 + topOffset - 60 &&
      rect.bottom <= document.documentElement.clientHeight) ||
    (rect.top <= topOffset &&
      rect.bottom >= document.documentElement.clientHeight)
  );
}

export function splitPascalCase(
  string: string,
  asArray = false
): string | string[] {
  const textAsArray = Array.from(string);
  textAsArray.forEach((char, index) => {
    if (
      char === char.toUpperCase() &&
      index > 0 &&
      textAsArray.at(index + 1) &&
      textAsArray.at(index + 1) !== textAsArray.at(index + 1)?.toUpperCase() &&
      textAsArray.at(index - 1) &&
      textAsArray.at(index - 1) !== ' '
    ) {
      textAsArray.splice(index, 0, ' ');
    }
  });
  const resultString = textAsArray.join('')?.trim();
  return asArray ? resultString.split(' ') : resultString;
}

export function toggleAccordion(
  container: HTMLElement,
  nextState?: boolean,
  params?: {
    transitionDuration?: number;
    easing?: 'ease-in' | 'ease-out' | 'ease-in-out' | 'none';
    callback?: () => void;
  }
): void {
  const easing =
    params?.easing === 'none' ? '' : params?.easing ?? 'ease-in-out';
  const duration = params?.transitionDuration ?? 200;

  const attPref = 'ling-accord-orig-';

  let origHeight: string | null;
  let origMaxHeight: string | null;
  let origTransition: string | null;

  const existingState: 'opened' | 'closed' | null = container.getAttribute(
    attPref + 'state'
  ) as any;

  const argumentState = nextState !== undefined;
  if (!argumentState) {
    if (existingState) {
      nextState = existingState === 'closed';
    } else {
      nextState = true;
    }
  }

  if (existingState) {
    origHeight = container.getAttribute(attPref + 'height');
    origMaxHeight = container.getAttribute(attPref + 'max-height');
    origTransition = container.getAttribute(attPref + 'transition');
  } else {
    const originalComputedStyles = getComputedStyle(container);
    origHeight =
      originalComputedStyles.height === 'none'
        ? '0px'
        : originalComputedStyles.height;
    origMaxHeight =
      originalComputedStyles.maxHeight === 'none'
        ? '0px'
        : originalComputedStyles.maxHeight;
    origTransition =
      originalComputedStyles.transition === 'none'
        ? ''
        : originalComputedStyles.transition;

    container.setAttribute(attPref + 'height', origHeight);
    container.setAttribute(attPref + 'max-height', origMaxHeight);
    container.setAttribute(attPref + 'transition', origTransition);
  }

  container.style.overflow = 'hidden';

  if (nextState) {
    if (existingState === 'opened') {
      return;
    }
    container.setAttribute(attPref + 'state', 'opened');
    container.style.height = origHeight ?? 0 + 'px';
    container.style.maxHeight = origMaxHeight ?? 0 + 'px';
    if (origMaxHeight === '0px' && origHeight && origHeight !== '0px') {
      container.style.maxHeight = origHeight;
    }
    container.style.transition = `height ${duration}ms ${easing},  max-height ${duration}ms ${easing}`;
    requestAnimationFrame(() => {
      container.style.height = container.scrollHeight + 'px';
      container.style.maxHeight = container.scrollHeight + 'px';
      setTimeout(() => {
        if (container.style.height === origHeight) {
          return;
        }
        container.style.maxHeight = 'max-content';
        container.style.height = 'auto';
        container.style.transition = origTransition ?? '';
        container.style.overflow = 'visible';
        if (params?.callback) {
          requestAnimationFrame(params.callback);
        }
      }, duration);
    });
  } else {
    if (existingState === 'closed') {
      return;
    }
    container.setAttribute(attPref + 'state', 'closed');
    if (getComputedStyle(container).height === origHeight) {
      return;
    }
    requestAnimationFrame(() => {
      container.style.height = container.scrollHeight + 'px';
      container.style.maxHeight = container.scrollHeight + 'px';
      container.style.transition = `height ${duration}ms ${easing},  max-height ${duration}ms ${easing}`;
      requestAnimationFrame(() => {
        container.style.maxHeight = origMaxHeight ?? '0px';
        container.style.height = origHeight ?? '0px';
        setTimeout(() => {
          container.style.transition = origTransition ?? '';
          if (params?.callback) {
            requestAnimationFrame(params.callback);
          }
        }, duration);
      });
    });
  }
}

export function copyImageToBuffer(safeUrl?: SafeUrl): Observable<void> | null {
  if (!safeUrl) {
    return null;
  }

  return isSafari()
    ? from(copyImageToBufferSafari(safeUrl))
    : from(fetch((safeUrl as any).changingThisBreaksApplicationSecurity))
        .pipe(switchMap((res) => from(res.blob())))
        .pipe(
          switchMap((blob) =>
            navigator.clipboard.write([
              new ClipboardItem({
                ['image/png']: blob,
              }),
            ])
          )
        );
}

async function copyImageToBufferSafari(safeUrl?: SafeUrl): Promise<any> {
  if (!safeUrl) {
    return null;
  }
  await navigator.clipboard.write([
    new ClipboardItem({
      ['image/png']: getImageBlobFromUrl(
        (safeUrl as any).changingThisBreaksApplicationSecurity
      ),
    }),
  ]);
}

async function getImageBlobFromUrl(url: string) {
  const fetchedImageData = await fetch(url);
  return await fetchedImageData.blob();
}

export function isSafari() {
  return (
    navigator.vendor.match(/apple/i) &&
    !navigator.userAgent.match(/crios/i) &&
    !navigator.userAgent.match(/fxios/i) &&
    !navigator.userAgent.match(/Opera|OPT\//)
  );
}

export function numDigits(x: number): number {
  return (Math.log10((x ^ (x >> 31)) - (x >> 31)) | 0) + 1;
}

export function getActiveMenuItem(
  allItems: MenuItem[],
  router: Router
): MenuItem | undefined {
  const normalizeRoute = (route: string) =>
    route.startsWith('/') ? route : '/' + route;
  const possibleRoutes = allItems
    .map((item) => item.associatedRoutes)
    .flat()
    .map(normalizeRoute)
    .filter((route) => router.url.startsWith(route));

  if (!possibleRoutes.length) {
    return undefined;
  }

  const longestPossibleRoute = possibleRoutes.reduce((a, b) =>
    a.length > b.length ? a : b
  );

  return allItems.find((menuItem) =>
    menuItem.associatedRoutes.map(normalizeRoute).includes(longestPossibleRoute)
  );
}

export function toCamelCase(str: string): string {
  return str
    .replace(/^\w|[A-Z]|\b\w/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/[\s-]+/g, '');
}

export function scrollWithCallback(
  options: ScrollToOptions,
  callback: () => any
) {
  const fixedTop = options.top?.toFixed();
  const fixedLeft = options.left?.toFixed();
  const onScroll = function () {
    if (window.scrollY.toFixed() === fixedTop) {
      window.removeEventListener('scroll', onScroll);
      callback();
    }
  };

  window.addEventListener('scroll', onScroll);
  onScroll();
  window.scroll(options);
}

export function onboardingStepTextFactory(tourName: string) {
  return (stepName: string) => ({
    title: `onboardingTours.${tourName}.${stepName}.title`,
    content: `onboardingTours.${tourName}.${stepName}.content`,
  });
}

export function navigateToLoginWithNext(
  router: Router,
  activatedRoute: ActivatedRoute,
  next?: string,
  additionalQueryParams?: Partial<{ [key in QueryParamsKeys]: any }>
) {
  if (next?.startsWith('/login')) {
    next = activatedRoute.snapshot.queryParams[QueryParamsKeys.NextNavigation];
  }

  const nextRoute = (
    next ??
    activatedRoute.snapshot.queryParams[QueryParamsKeys.NextNavigation] ??
    router.url
  ).split('?')[0];

  router
    .navigate(['/login'], {
      queryParams: {
        ...additionalQueryParams,
        [QueryParamsKeys.NextNavigation]: nextRoute.startsWith('/login')
          ? undefined
          : nextRoute,
      },
      queryParamsHandling: 'merge',
    })
    .then();
}

export function getCoords(elem: Element) {
  // crossbrowser version
  const box = elem.getBoundingClientRect();

  const body = document.body;
  const docEl = document.documentElement;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;

  return { top: Math.round(top), left: Math.round(left) };
}

export function lottieAnimation(
  name: string,
  config?: Omit<AnimationOptions, 'path'>
): AnimationOptions {
  return config
    ? { ...config, path: `assets/lottie-animations/${name}.json` }
    : { path: `assets/lottie-animations/${name}.json` };
}

function encodeQueryData(params?: Record<string, any>) {
  if (!params || !Object.keys(params).length) {
    return '';
  }

  return `?${Object.entries(params)
    .filter(([key, value]) => !!value)
    .map(
      ([key, value]) =>
        encodeURIComponent(key) + '=' + encodeURIComponent(value)
    )
    .join('&')}`;
}

export function getThisFrontUrl(
  path?: string | string[],
  params?: Record<string, any>
) {
  return (
    location.origin +
    '/' +
    (Array.isArray(path) ? path.join('/') : path) +
    encodeQueryData(params)
  );
}

export function getCertificatePreviewLink(certificate?: Certificate | null) {
  if (!certificate || !certificate._id) {
    return;
  }
  return `${getThisFrontUrl(['preview', certificate._id], {
    claimToken: certificate.token,
  })}`;
}

export function getDomainFromUrl(url: string) {
  const a = document.createElement('a');
  a.setAttribute('href', url);
  const hostname = a.hostname;
  a.remove();
  return hostname;
}

export function parseLinksHtml(
  text: string,
  sanitizer: DomSanitizer,
  displayRawLink = false
) {
  const linkRegex = new RegExp(LingRegExps.linkFromUserInput);
  const splitByLink = text.split(linkRegex);
  const parsed = splitByLink
    .map((string) =>
      linkRegex.test(string)
        ? `<a class="ling-activated-link" target="_blank" data-unsafe-link="true" href="${sanitizer.sanitize(
            SecurityContext.URL,
            ensureUrlHasProtocol(string)
          )}">${
            displayRawLink
              ? ensureUrlHasProtocol(string)
              : getDomainFromUrl(ensureUrlHasProtocol(string))
          }</a>`
        : sanitizer.sanitize(SecurityContext.HTML, string)
    )
    .join('');
  return sanitizer.bypassSecurityTrustHtml(parsed);
}

export function ensureUrlHasProtocol(url: string) {
  const allowedProtocols = ['https://', 'http://'];
  return allowedProtocols.some((protocol) => url.startsWith(protocol))
    ? url
    : `https://${url}`;
}

export function scrollTop(): void {
  window.scroll({
    top: 0,
    behavior: 'smooth',
  });
}
